import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { signIn, signOut, type SignInOutput } from 'aws-amplify/auth';
import { useMutation } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';

import { ErrorMessage } from 'components/ErrorMessage';
import { PasswordInput } from 'components/PasswordInput';
import { useValidationSchema } from 'hooks/useValidationSchema';
import { errorToast } from 'helpers/toast';
import { messages } from '../../messages';
import { getValidationSchema } from './validators';

interface FormTypes {
  password: string;
}

interface Props {
  onSuccess(): void;
  onMFA(smsDestination: string | undefined, password: string): void;
  onNotConfirmed(password: string): void;
  onForgotPassword(): void;
  onBack(): void;
  username: string;
}

export const PasswordStep = memo(
  ({
    onSuccess,
    onMFA,
    onNotConfirmed,
    onForgotPassword,
    onBack,
    username,
  }: Props) => {
    const { t } = useTranslation();
    const validationSchema = useValidationSchema(getValidationSchema);
    const { mutate, isLoading, error } = useMutation<
      SignInOutput,
      { name?: string },
      FormTypes
    >({
      mutationFn: async ({ password }) => {
        // Defensive: clear any lingering Amplify session before signing in so
        // v6 signIn can't throw UserAlreadyAuthenticatedException (which would
        // leave the button silently dead). The username step already does this,
        // but a stale session must never reach signIn unguarded.
        await signOut().catch(() => undefined);
        return signIn({ username, password });
      },
    });

    const submitForm = (form: FormTypes) => {
      mutate(form, {
        onSuccess: ({ isSignedIn, nextStep }) => {
          if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE')
            onMFA(nextStep.codeDeliveryDetails?.destination, form.password);
          else if (nextStep.signInStep === 'CONFIRM_SIGN_UP')
            onNotConfirmed(form.password);
          else if (isSignedIn) onSuccess();
          // Any other next step (TOTP, force-change-password, …) is not
          // supported by this UI — surface it instead of silently treating an
          // unfinished sign-in as success.
          else
            errorToast(t(messages.errorOccurredTryAgain()), {
              position: 'top-right',
            });
        },
        onError: e => {
          if (e?.name === 'UserNotConfirmedException')
            onNotConfirmed(form.password);
        },
      });
    };

    const mapError = () => {
      if (error?.name === 'NotAuthorizedException')
        return t(messages.incorrectPassword());
    };

    return (
      <>
        <Formik
          initialValues={{ password: '' } as FormTypes}
          onSubmit={submitForm}
          validationSchema={validationSchema}
          validateOnChange={false}
        >
          <Form data-cy="password-step">
            <Stack spacing="2.4rem" direction="column" justifyContent="center">
              <Typography variant="h1">{t(messages.login())}</Typography>
              <Typography
                variant="body1"
                style={{ marginTop: '1.6rem' }}
                sx={{ color: 'text.secondary' }}
              >
                {username}
              </Typography>
              <PasswordInput
                name="password"
                validationSchema={validationSchema}
                oneWayValidationOnChange={true}
                data-cy="password-input"
              />
              <ErrorMessage
                message={mapError()}
                style={{ marginTop: '0.8rem' }}
              />
              <LoadingButton
                type="submit"
                loading={isLoading}
                color="primary"
                variant="contained"
                data-cy="password-step-submit-button"
              >
                {t(messages.next())}
              </LoadingButton>
              <Button
                variant="text"
                onClick={onForgotPassword}
                data-cy="forgot-password-button"
              >
                {t(messages.forgotPassword())}
              </Button>
              <Button
                variant="text"
                onClick={onBack}
                data-cy="back-button"
                style={{ marginTop: '3.7rem' }}
              >
                {t(messages.back())}
              </Button>
            </Stack>
          </Form>
        </Formik>
      </>
    );
  },
);
